import { type ReactNode } from "react"
import { useKeycloak } from "@react-keycloak/web"
import { Spinner } from "@/components/ui/spinner"

interface AuthGuardProps {
  children: ReactNode
}

/**
 * AuthGuard Component
 *
 * Protects routes by ensuring users are authenticated before accessing them.
 * Shows a loading state during initialization and automatically redirects
 * unauthenticated users to Keycloak login.
 *
 * @param children - Protected content to render for authenticated users
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { keycloak, initialized } = useKeycloak()

  // Show loading state during Keycloak initialization
  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner className="mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // If not authenticated, Keycloak will handle redirect
  // This should not normally render due to 'login-required' init option
  if (!keycloak.authenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner className="mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    )
  }

  // User is authenticated, render protected content
  return <>{children}</>
}

